import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted } from 'vue'
  import type { ComputedRef } from 'vue'

  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import { vOnClickOutside } from '@vueuse/components'
  import Search from '@/components/Search.vue'

  import { useRoute, useRouter } from 'vue-router/composables'
  import useIcons from '@/composables/useIcons'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useCourse from '@/composables/useCourse'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useI18n from '@/composables/useI18n'

  import { UserModule } from '@/store/modules/user'
  import { CourseModule } from '@/store/modules/course'

  import type { CourseBundleOverview, CourseBundleCourseOverview } from '@/services/interfaces/Course'
  import useShopifyClient from '@/composables/useShopifyClient'

  interface NavigationItem {
    to?: string
    icon?: ComputedRef<string>
    label: string
    isExpandable?: boolean
    isCourseBundles?: boolean
    isAcademyAdmin?: boolean
    subItems?: NavigationItem[]
  }

  
export default /*#__PURE__*/_defineComponent({
  __name: 'Sidebar',
  props: {
    isOpen: { type: Boolean }
  },
  emits: ['close-sidebar'],
  setup(__props: any, { emit }) {

  const route = useRoute()
  const router = useRouter()
  const {
    closeIcon,
    homeIcon,
    allBookmarks,
    settingsIcon,
    arrowRight,
    graduationCapIcon,
    course34iIcon,
    course34dIcon,
    course34fIcon,
    courseAevoIcon,
  } = useIcons()
  const { isMobileDevice, isDesktopDevice } = useBreakpoint()
  const { getPackageBundle, sortedCourseBundlesOverview, courseBundle, onCourseLayout, trialTimeLeft, isTrialExpired } =
    useCourse()
  const {
    extractCourseVertical,
    showReTriggerOnboardingPopup,
    showCompanyOnboardingPopupDetails,
    hasValidUser,
    isLtiUser,
    scrollToTop,
    is34ACompany,
  } = useCommonMixin()
  const { handleWebsiteCheckout } = useShopifyClient()
  const { translateString } = useI18n()

  

  

  const expandedIndex = ref<number | null>(1)

  const onDashboard = computed(() => route.name === 'Dashboard')

  const navigationItems = computed<NavigationItem[]>(() => {
    const items: NavigationItem[] = [
      {
        to: '/',
        icon: homeIcon,
        label: 'navigation.sidebar.dashboard',
      },
    ]

    if (!is34ACompany.value) {
      items.push({
        icon: graduationCapIcon,
        label: 'navigation.sidebar.examPreparation',
        isExpandable: true,
        isCourseBundles: true,
      })
    }

    if (UserModule.isCompanyEditor && isDesktopDevice.value) {
      items.push({
        label: 'navigation.sidebar.manageCourses',
        to: '/editor',
      })
    }

    if (UserModule.isCompanyAdmin && isDesktopDevice.value) {
      items.push({
        label: 'navigation.sidebar.academyAdmin',
        isExpandable: true,
        isAcademyAdmin: true,
        subItems: [
          {
            to: '/admin/users-and-groups',
            label: 'navigation.admin.usersAndGroups',
          },
          {
            to: '/admin/gamification',
            label: 'navigation.admin.gamification',
          },
        ],
      })
    }

    if (hasValidUser.value) {
      items.push({
        to: '/bookmarks',
        icon: allBookmarks,
        label: 'navigation.sidebar.bookmarks',
      })

      if (!isLtiUser.value) {
        items.push({
          to: '/account',
          icon: settingsIcon,
          label: 'account.accountSettings',
        })
      }

      if (UserModule.companies.length > 1) {
        items.push({
          to: '/company-selection',
          label: 'navigation.mainNav.switchAcademy',
        })
      }
    }

    return items
  })

  const courseVerticalIcon = computed(() => (title: string) => {
    const vertical = extractCourseVertical(title)
    if (vertical === '34i') return course34iIcon.value
    if (vertical === '34d') return course34dIcon.value
    if (vertical === '34f') return course34fIcon.value
    return courseAevoIcon.value
  })

  const isActiveCourseBundle = computed(() => (bundleId: string) => {
    return courseBundle.value?.courseBundleId === bundleId && (onCourseLayout.value || onDashboard.value)
  })

  const hasTrialCourse = computed(() => (bundle: CourseBundleOverview) => {
    return bundle.courses.some((course) => course.trial)
  })

  const getCourseTypePrefix = (courseType: string) => {
    return courseType === 'written' ? 'Schriftlich: ' : 'Mündlich: '
  }

  const getTrialStatus = (course: CourseBundleCourseOverview, typePrefix: string) => {
    const { days } = trialTimeLeft.value(course)

    if (isTrialExpired.value(course)) {
      return {
        text: typePrefix + translateString('testExpired') + '. ->',
        action: translateString('selectCourse'),
        isExpired: true,
        courseType: course.courseType,
      }
    }

    return {
      text:
        typePrefix +
        translateString('testDaysRemaining', {
          days,
          testDayText: days === 1 ? translateString('testDay') : translateString('testDays'),
        }),
      action: null,
      isExpired: false,
      courseType: course.courseType,
    }
  }

  const getProgressStatus = (course: CourseBundleCourseOverview, typePrefix: string, addComma: boolean) => {
    const progress = Math.round((course.numberOfLessonsCompleted / course.numberOfLessons) * 100)

    return {
      text: typePrefix + `${progress}%` + (addComma ? ', ' : ''),
      action: null,
      isExpired: false,
      courseType: course.courseType,
    }
  }

  const getEmptyStatus = (course: CourseBundleCourseOverview) => {
    return {
      text: '',
      action: null,
      isExpired: false,
      courseType: course.courseType,
    }
  }

  const bundleStatus = computed(() => (bundle: CourseBundleOverview) => {
    // Sort courses by type (written first, then oral)
    const sortedCourses = [...bundle.courses].sort((a, b) => {
      if (a.courseType === 'written' && b.courseType === 'oral') return -1
      if (a.courseType === 'oral' && b.courseType === 'written') return 1
      return 0
    })

    const hasAnyTrial = sortedCourses.some((course) => course.trial)

    return sortedCourses.map((course, index) => {
      const typePrefix = getCourseTypePrefix(course.courseType)
      const isLastItem = index === sortedCourses.length - 1
      const addComma = !hasAnyTrial && !isLastItem

      if (course.trial) {
        return getTrialStatus(course, typePrefix)
      }

      if (course.purchased || course.expired || !course.trial) {
        return getProgressStatus(course, typePrefix, addComma)
      }

      return getEmptyStatus(course)
    })
  })

  const toggleExpand = (index: number) => {
    expandedIndex.value = expandedIndex.value === index ? null : index
  }

  const closeSidebar = () => {
    emit('close-sidebar')
  }

  const closeSidebarAndResetExpandedIndex = () => {
    closeSidebar()
    expandedIndex.value = null
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeSidebar()
    }
  }

  const setCourseBundleAndCourse = async (bundleId: string) => {
    if (courseBundle.value?.courseBundleId === bundleId) {
      if (!onCourseLayout.value && !onDashboard.value) {
        router.push({ name: 'Dashboard' })
      }

      closeSidebar()
      return
    }

    if (!onDashboard.value) {
      router.push({ name: 'Dashboard' })
    }

    CourseModule.setCourse(null)
    CourseModule.setCourseBundle(null)

    scrollToTop()

    closeSidebar()

    const bundle = await getPackageBundle(bundleId)

    if (bundle?.courses) {
      const purchasedCourse = bundle.courses.find((course) => course.purchased)
      if (purchasedCourse) {
        CourseModule.setCourse(purchasedCourse)
      } else {
        const writtenCourse = bundle.courses.find((course) => course.courseType === 'written')
        if (writtenCourse) {
          CourseModule.setCourse(writtenCourse)
        }
      }
    }
  }

  const triggerOnboardingPopup = () => {
    closeSidebar()
    showCompanyOnboardingPopupDetails()
  }

  const setActiveExpandableIndex = () => {
    const activeExpandableIndex = navigationItems.value.findIndex((item) => {
      if (!item.isExpandable) return false

      if (item.isCourseBundles) {
        return sortedCourseBundlesOverview.value.some((bundle) => isActiveCourseBundle.value(bundle.courseBundleId))
      }

      if (item.isAcademyAdmin) {
        return item.subItems?.some((subItem) => route.path.startsWith(subItem.to || ''))
      }

      return false
    })

    expandedIndex.value = activeExpandableIndex !== -1 ? activeExpandableIndex : 1
  }

  const createCheckout = async (bundle: CourseBundleOverview) => {
    const course = bundle.courses[0]
    if (course?.id) {
      await handleWebsiteCheckout(course.id)
    }
  }

  onMounted(() => {
    setActiveExpandableIndex()
  })

return { __sfc: true,route, router, closeIcon, homeIcon, allBookmarks, settingsIcon, arrowRight, graduationCapIcon, course34iIcon, course34dIcon, course34fIcon, courseAevoIcon, isMobileDevice, isDesktopDevice, getPackageBundle, sortedCourseBundlesOverview, courseBundle, onCourseLayout, trialTimeLeft, isTrialExpired, extractCourseVertical, showReTriggerOnboardingPopup, showCompanyOnboardingPopupDetails, hasValidUser, isLtiUser, scrollToTop, is34ACompany, handleWebsiteCheckout, translateString, emit, expandedIndex, onDashboard, navigationItems, courseVerticalIcon, isActiveCourseBundle, hasTrialCourse, getCourseTypePrefix, getTrialStatus, getProgressStatus, getEmptyStatus, bundleStatus, toggleExpand, closeSidebar, closeSidebarAndResetExpandedIndex, handleKeydown, setCourseBundleAndCourse, triggerOnboardingPopup, setActiveExpandableIndex, createCheckout, computed, ref, onMounted, SVGRenderer, vOnClickOutside, Search, useRoute, useRouter, useIcons, useBreakpoint, useCourse, useCommonMixin, useI18n, UserModule, CourseModule, useShopifyClient }
}

})